import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';
import Logo from "../assets/logo/logo.png";

const Navigation = () => {
  const [isDark, setIsDark] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    let shouldBeDark = savedDarkMode ? savedDarkMode === 'true' : window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    setIsDark(shouldBeDark);
    document.documentElement.classList.toggle('dark', shouldBeDark);

    // Hide hint after 6 seconds
    const timeout = setTimeout(() => setShowHint(false), 6000);
    return () => clearTimeout(timeout);
  }, []);

  const toggleDarkMode = () => {
    const newMode = !isDark;
    setIsDark(newMode);
    localStorage.setItem('darkMode', newMode.toString());
    document.documentElement.classList.toggle('dark', newMode);
  };

  return (
    <nav className="relative bg-white/70 dark:bg-gray-900/70 backdrop-blur-2xl border-b border-white/20 dark:border-white/10 shadow-2xl sticky top-0 z-50 transition-all duration-300">
      {/* Ambient background effects */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-1/4 w-16 h-16 rounded-full blur-2xl animate-pulse bg-[#4285F4]"></div>
        <div className="absolute bottom-0 right-1/3 w-12 h-12 rounded-full blur-xl animate-pulse bg-[#EA4335]" style={{ animationDelay: '1s' }}></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex justify-between h-16 items-center">

          {/* Logo with Google Colors */}
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center group">
              <img src={Logo} alt="GLabs Automator Logo" className="h-16 w-16 transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110"/>
              <span className="ml-2 text-2xl font-bold tracking-tight">
                <span className="text-[#4285F4]">G</span>
                <span className="text-[#EA4335]">L</span>
                <span className="text-[#FBBC04]">a</span>
                <span className="text-[#4285F4]">b</span>
                <span className="text-[#34A853]">s</span>
                <span className="text-gray-900 dark:text-white"> Automator</span>
              </span>
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-2">

            {isHomePage ? (
              <>
                <a href="#features" className="relative group text-gray-800 dark:text-white px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 hover:bg-white/30 dark:hover:bg-gray-800/30 ripple-effect">
                  <span className="relative z-10">Features</span>
                </a>
                <a href="#how-it-works" className="relative group text-gray-800 dark:text-white px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 hover:bg-white/30 dark:hover:bg-gray-800/30 ripple-effect">
                  <span className="relative z-10">How It Works</span>
                </a>
                <a href="#faq" className="relative group text-gray-800 dark:text-white px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 hover:bg-white/30 dark:hover:bg-gray-800/30 ripple-effect">
                  <span className="relative z-10">FAQ</span>
                </a>
              </>
            ) : (
              <>
                <Link to="/#features" className="relative group text-gray-800 dark:text-white px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 hover:bg-white/30 dark:hover:bg-gray-800/30 ripple-effect">
                  <span className="relative z-10">Features</span>
                </Link>
                <Link to="/#how-it-works" className="relative group text-gray-800 dark:text-white px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 hover:bg-white/30 dark:hover:bg-gray-800/30 ripple-effect">
                  <span className="relative z-10">How It Works</span>
                </Link>
                <Link to="/#faq" className="relative group text-gray-800 dark:text-white px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 hover:bg-white/30 dark:hover:bg-gray-800/30 ripple-effect">
                  <span className="relative z-10">FAQ</span>
                </Link>
              </>
            )}

            {/* Dark Mode Toggle with Material Design */}
            <div className="relative flex items-center">
              <button
                onClick={toggleDarkMode}
                className={`p-2.5 rounded-xl transition-all duration-300 hover:bg-white/30 dark:hover:bg-gray-800/30 transform hover:scale-110 ripple-effect
                  ${showHint ? 'ring-2 ring-[#4285F4] dark:ring-[#FBBC04] animate-pulse' : ''}
                `}
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? (
                  <Sun className="text-[#FBBC04] h-5 w-5 drop-shadow-md" />
                ) : (
                  <Moon className="text-[#4285F4] h-5 w-5 drop-shadow-md" />
                )}
              </button>

              {/* Animated hint tooltip */}
              {showHint && (
                <div className="absolute -top-12 left-1/2 -translate-x-1/2 animate-fadeInUp">
                  <div className="text-xs bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-3 py-1.5 rounded-lg shadow-2xl font-medium">
                    Try Dark Mode!
                  </div>
                </div>
              )}
            </div>

            {/* Download Button with Google Material Style */}
            <a
              href="https://chromewebstore.google.com/detail/glabs-automator-automate/lgijlcogpfkinhgaijjbocflajljjpen?utm_source=item-share-cb"
              target='_blank'
              rel="noreferrer"
              className="relative group overflow-hidden bg-gradient-to-r from-[#34A853] to-[#0f9d58] dark:from-[#8B5CF6] dark:to-[#7C3AED] text-white font-semibold px-6 py-2.5 rounded-xl text-sm transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 ripple-effect"
            >
              <span className="relative z-10">Download Now</span>
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
